import { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import CustomModal from '@/DesignSystem/components/custom-modal/CustomModal';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import axiosApiInstance from '@/api/shared/axios-instance';
import URL_FACTORY from '@/helpers/url-helper';
import { getTeamImageById } from '@/helpers/images-helper';
import useTeam from '@/hooks/use-team';
import { translate } from '@/data/fixture-transalations';
import { getTimeShortWithMinutes } from '@/helpers/datetime-helper';
import { getCompetitionImage } from '@/helpers/leagues-helper';

const DificultadBox = ({ label, value, color }) => (
    <Box
        sx={{
            flex: 1,
            p: 1,
            borderRadius: 1,
            borderTop: `4px solid ${color}`,
            backgroundColor: 'background.paper',
            textAlign: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
    >
        <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 500, color: 'text.primary' }}>
            {value}
        </Typography>
        <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', mb: 0.5 }}>
            {label}
        </Typography>
    </Box>
);

const ProximosPartidosModal = ({ open, onClose, teamId }) => {
    const [loading, setLoading] = useState(false);
    const [partidosData, setPartidosData] = useState([]);

    const { teamName } = useTeam(teamId);

    useEffect(() => {
        if (open && teamId) {
            fetchProximosPartidos();
        }
    }, [open, teamId]);

    const fetchProximosPartidos = async () => {
        try {
            setLoading(true);
            const response = await axiosApiInstance.post(URL_FACTORY.ObtenerProximosPartidos, {
                teamId: teamId,
            });

            if (response.data && response.data.partidos) {
                setPartidosData(response.data.partidos);
            }
        } catch (error) {
            console.error('Error fetching próximos partidos:', error);
        } finally {
            setLoading(false);
        }
    };

    const getDificultadColor = (value) => {
        if (value >= 90) return '#1d6fb1'; // Azul - Excelente
        if (value >= 70) return '#00a04f'; // Verde - Muy Bueno
        if (value >= 50) return 'rgb(109 156 133)'; // Verde claro - Bueno
        if (value >= 30) return 'rgb(255, 167, 38)'; // Amarillo - Regular
        return '#ab1414'; // Rojo - Muy Malo
    };

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={`Próximos Partidos - ${teamName}`}
            maxWidth="md"
            width="95%"
            paperSx={{
                minHeight: '400px',
            }}
            contentSx={{ pt: 2 }}
            padding={1}
        >
            {loading ? (
                <Box
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
                >
                    <CircularProgress sx={{ color: '#a126da' }} />
                </Box>
            ) : partidosData.length > 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {partidosData.map((partido) => {
                        const isHome = partido.home.id === teamId;
                        const rival = isHome ? partido.away : partido.home;

                        return (
                            <Box
                                key={partido.fixtureId}
                                sx={{
                                    p: 1,
                                    borderRadius: 2,
                                    backgroundColor: 'background.default',
                                    border: '1px solid',
                                    borderColor: 'border.light',
                                }}
                            >
                                {/* Header con equipos */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                        <CustomImageComponent
                                            src={getTeamImageById(partido.home.id)}
                                            alt={partido.home.nombre}
                                            width={30}
                                            height={30}
                                        />
                                        <Typography
                                            variant="body2"
                                            sx={{ fontSize: '14px', fontWeight: 500 }}
                                        >
                                            {partido.home.nombre}
                                        </Typography>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                            mx: 2,
                                        }}
                                    >
                                        vs
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            flex: 1,
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{ fontSize: '14px', fontWeight: 500 }}
                                        >
                                            {partido.away.nombre}
                                        </Typography>
                                        <CustomImageComponent
                                            src={getTeamImageById(partido.away.id)}
                                            alt={partido.away.nombre}
                                            width={30}
                                            height={30}
                                        />
                                    </Box>
                                </Box>

                                {/* Fecha y Jornada */}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: '13px', color: 'text.secondary' }}
                                    >
                                        📅 {getTimeShortWithMinutes(partido.fechaPartido)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontSize: '13px', color: 'text.secondary' }}
                                    >
                                        <CustomImageComponent
                                            src={getCompetitionImage(partido.leagueId)}
                                            alt={partido.league}
                                            width={12}
                                            height={12}
                                        />
                                        {translate(partido.round)}
                                    </Typography>
                                </Box>

                                {/* Dificultad */}
                                <Box>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            mb: 1,
                                            color: 'text.primary',
                                        }}
                                    >
                                        Dificultad vs {rival.nombre}:
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <DificultadBox
                                            label="Delantera"
                                            value={partido.dificultad.delantera}
                                            color={getDificultadColor(partido.dificultad.delantera)}
                                        />
                                        <DificultadBox
                                            label="Mediocampo"
                                            value={partido.dificultad.mediocampo}
                                            color={getDificultadColor(partido.dificultad.mediocampo)}
                                        />
                                        <DificultadBox
                                            label="Defensa"
                                            value={partido.dificultad.defensa}
                                            color={getDificultadColor(partido.dificultad.defensa)}
                                        />
                                        <DificultadBox
                                            label="Portería"
                                            value={partido.dificultad.porteria}
                                            color={getDificultadColor(partido.dificultad.porteria)}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            ) : (
                <Box
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
                >
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        No hay próximos partidos disponibles
                    </Typography>
                </Box>
            )}
        </CustomModal>
    );
};

export default ProximosPartidosModal;
